import { useQuery } from '@tanstack/react-query';
import { ProjectResponseType } from '@/types/apiTypes';
import { axiosClient } from '../axiosClient';
import * as Sentry from '@sentry/react';
import { SalesReferencesSoldSortType } from '@/types/apiTypes';
import {
  SoldRefDataType,
  SoldWeightDataType,
  AdsRefDataType,
  AdsWeightDataType,
  AdsFilterDataType,
  setCurrentProjectType,
  CoordinatesType,
  SoldFilterDataType,
  MarketFilterDataType,
} from '@/types/dataTypes';
import {
  getAdsFilterParamsFromApiData,
  getCoordinatesLatLng,
  getMarketsFilterParamsFromApiData,
  getSoldFilterParamsFromApiData,
} from '@/helpers';

export interface SalesRefResponse {
  soldParams: {
    refData: Partial<SoldRefDataType>;
    weightData: Partial<SoldWeightDataType>;
    filterData: Partial<SoldFilterDataType>;
  };
  adsParams: {
    refData: Partial<AdsRefDataType>;
    weightData: Partial<AdsWeightDataType>;
    filterData: Partial<AdsFilterDataType>;
  };
  marketParams: {
    filterData: Partial<MarketFilterDataType> | undefined;
  };
  project: setCurrentProjectType;
  coordinates: CoordinatesType;
}

const useProject = (projectId?: string) => {
  return useQuery({
    queryKey: ['project', projectId],
    queryFn: async (): Promise<SalesRefResponse | null> => {
      if (!projectId || projectId === undefined) return null;

      const URL = `${import.meta.env.VITE_API_PROJECTS_URL}/${projectId}`;
      const { data } = await axiosClient.get<ProjectResponseType>(URL);
      if (!data) return null;
      const {
        ad_references_search_criteria: adsSearchCritera,
        sale_references_search_criteria: salesSearchCritera,
        ads_market_share_search_criteria: marketSearchCritera,
        index_normalized_search_criteria: indexValues,
      } = data.project;
      const saleRefSortFromData:SalesReferencesSoldSortType | null = data.project.sale_references_sort as SalesReferencesSoldSortType;
      const sale_references_sort = saleRefSortFromData ? {
        id: saleRefSortFromData.field,
        desc: (saleRefSortFromData.sort_order === "DESC" ? true : false)
      } : null;
      const { soldRefValues, soldWeightValues, soldFilterValues } =
        getSoldFilterParamsFromApiData(salesSearchCritera);

      const { adsRefValues, adsWeightValues, adsFilterValues } =
        getAdsFilterParamsFromApiData(adsSearchCritera);

      const { marketFilterValues } =
        getMarketsFilterParamsFromApiData(marketSearchCritera) || {};

      const coordinates = getCoordinatesLatLng(
        salesSearchCritera?.wgs84lat,
        salesSearchCritera?.wgs84lon
      );

      const soldParams = {
        refData: soldRefValues,
        weightData: soldWeightValues,
        filterData: soldFilterValues,
      };
      const adsParams = {
        refData: adsRefValues,
        weightData: adsWeightValues,
        filterData: adsFilterValues,
      };

      const marketParams = {
        filterData: marketFilterValues,
      };
      const project = {
        ...data.project,
        excludedSoldIds: salesSearchCritera?.excluded_ids ?? [],
        selectedSoldIds: salesSearchCritera?.selected_ids ?? [],
        excludedAdsIds: adsSearchCritera?.excluded_ids ?? [],
        selectedAdsIds: adsSearchCritera?.selected_ids ?? [],
        adsPolygons: adsSearchCritera?.polygons?.features[0]?.geometry?.coordinates[0][0] || null,
        soldPolygons: salesSearchCritera?.polygons?.features[0]?.geometry?.coordinates[0][0] || null,
        marketPolygons: marketSearchCritera?.polygons?.features[0]?.geometry?.coordinates[0][0] || null,
        indexValues,
        sale_references_sort,
        ads_market_share_search_criteria: marketSearchCritera,
      };

      return { soldParams, adsParams, marketParams, project, coordinates };
    },
    onError: (error) => {
      Sentry.captureException(error);
    }
  }
  );
};

export default useProject;
